/**
 * FeedbackWidget — "Flag an issue / suggest an improvement" mini-form.
 *
 * No auth required. Honeypot field (`website`) + backend per-IP rate limit
 * (5/min) guard against spam. Attaches the current project or run context.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";

export const FeedbackWidget = ({ projectId, runId, context, language = "en" }) => {
  const [open, setOpen] = useState(false);
  const [expected, setExpected] = useState("");
  const [got, setGot] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (expected.trim().length < 3 || got.trim().length < 3) {
      toast.error(language === "fr" ? "Merci de remplir les deux champs" : "Please fill both fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        expected: expected.trim(),
        got: got.trim(),
        email: email.trim() || undefined,
        project_id: projectId || undefined,
        run_id: runId || undefined,
        context: context || undefined,
        website: website || undefined,
      });
      track("feedback_submitted", { hasEmail: !!email });
      setSent(true);
      toast.success(language === "fr" ? "Merci — nous avons bien reçu vos commentaires" : "Thanks — we've got your feedback");
      setExpected(""); setGot(""); setEmail("");
    } catch (e) {
      const msg = e?.response?.data?.detail || (language === "fr" ? "Envoi impossible" : "Could not submit");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="mb-6 text-right">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-secondary !py-2 !px-3 text-sm"
          data-testid="feedback-open-btn"
        >
          <MessageSquarePlus size={14} />
          {language === "fr" ? "Signaler un problème / suggérer" : "Flag an issue / suggest an improvement"}
        </button>
      </div>
    );
  }

  return (
    <section className="card p-6 mb-6" data-testid="feedback-widget"
              style={{ background: "var(--paper)" }}>
      {sent ? (
        <div className="text-sm" style={{ color: "var(--brand-green)" }} data-testid="feedback-sent">
          {language === "fr"
            ? "Merci — un membre de l'équipe reviendra vers vous si vous avez laissé un email."
            : "Thanks — a member of our team will follow up if you left an email."}
          <button type="button" onClick={() => { setOpen(false); setSent(false); }}
                    className="ml-3 text-xs underline" style={{ color: "var(--muted)" }}>
            {language === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="feedback-form">
          <div>
            <div className="overline mb-1">
              {language === "fr" ? "Vos commentaires" : "Your feedback"}
            </div>
            <div className="font-display text-lg" style={{ color: "var(--char)" }}>
              {language === "fr" ? "Signaler un problème / suggérer une amélioration" : "Flag an issue / suggest an improvement"}
            </div>
          </div>

          <label className="block">
            <span className="text-xs" style={{ color: "var(--muted)" }}>
              {language === "fr" ? "Attendu" : "Expected"}
            </span>
            <textarea rows={3} value={expected} onChange={(e) => setExpected(e.target.value)}
                        maxLength={2000}
                        placeholder={language === "fr" ? "Ce que vous vous attendiez à voir…" : "What you expected to see…"}
                        className="w-full mt-1 px-3 py-2 border border-[var(--rule)] rounded text-sm"
                        data-testid="feedback-expected" />
          </label>

          <label className="block">
            <span className="text-xs" style={{ color: "var(--muted)" }}>
              {language === "fr" ? "Ce que vous avez obtenu" : "What you got instead"}
            </span>
            <textarea rows={3} value={got} onChange={(e) => setGot(e.target.value)}
                        maxLength={2000}
                        placeholder={language === "fr" ? "Ce que vous avez vu dans l'application…" : "What the app actually showed…"}
                        className="w-full mt-1 px-3 py-2 border border-[var(--rule)] rounded text-sm"
                        data-testid="feedback-got" />
          </label>

          <label className="block">
            <span className="text-xs" style={{ color: "var(--muted)" }}>
              {language === "fr" ? "Email (optionnel — pour un suivi)" : "Email (optional — for a follow-up)"}
            </span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full mt-1 px-3 py-2 border border-[var(--rule)] rounded text-sm"
                    data-testid="feedback-email" />
          </label>

          {/* Honeypot — hidden from real users via CSS, bots often fill every field */}
          <label style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
            Website
            <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1} autoComplete="off" />
          </label>

          <div className="flex items-center gap-2 justify-end">
            <button type="button" onClick={() => setOpen(false)}
                      className="btn-secondary !py-2 !px-3 text-sm" data-testid="feedback-cancel-btn">
              {language === "fr" ? "Annuler" : "Cancel"}
            </button>
            <button type="submit" className="btn-primary !py-2 !px-4 text-sm" disabled={busy}
                      data-testid="feedback-submit-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {language === "fr" ? "Envoyer" : "Send"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};
